import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Tags, AlertTriangle, Building2, Zap, Search } from 'lucide-react';
import PageHeader from './PageHeader';
import { SEVERITY_COLORS, getCategoryColor, normalizeSeverity } from '../constants/colors';

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low'];

function AnalyticsView({ records, categoryChartData, companyStats, onCategoryClick }) {
  const [selectedCompany, setSelectedCompany] = useState('');

  // Get all unique companies
  const allCompanies = useMemo(() => {
    return [...new Set(records.map(r => r.company))].sort();
  }, [records]);

  // Filter records by selected company
  const filteredRecords = useMemo(() => {
    if (!selectedCompany) return records;
    return records.filter(r => r.company === selectedCompany);
  }, [records, selectedCompany]);

  // Calculate overall stats
  const overallStats = useMemo(() => {
    return {
      totalRecords: records.length,
      companies: allCompanies.length,
      critical: records.filter(r => normalizeSeverity(r.severity) === 'critical').length,
      categories: [...new Set(records.map(r => r.category))].length,
    };
  }, [records, allCompanies]);

  // Calculate filtered stats
  const filteredStats = useMemo(() => {
    return {
      totalRecords: filteredRecords.length,
      critical: filteredRecords.filter(r => normalizeSeverity(r.severity) === 'critical').length,
      high: filteredRecords.filter(r => normalizeSeverity(r.severity) === 'high').length,
      medium: filteredRecords.filter(r => normalizeSeverity(r.severity) === 'medium').length,
    };
  }, [filteredRecords]);

  // Category data for selected company (or all if none selected)
  const coloredCategoryData = useMemo(() => {
    const categoryCount = {};
    filteredRecords.forEach(r => {
      categoryCount[r.category] = (categoryCount[r.category] || 0) + 1;
    });
    const labels = Object.keys(categoryCount).sort((a, b) => categoryCount[b] - categoryCount[a]);
    
    return {
      labels,
      datasets: [{
        label: 'Records by Category',
        data: labels.map(l => categoryCount[l]),
        backgroundColor: labels.map(label => getCategoryColor(label).hex),
        borderRadius: 6,
        borderSkipped: false,
      }],
    };
  }, [filteredRecords]);

  // Severity data for selected company
  const severityBarData = useMemo(() => {
    const counts = {};
    filteredRecords.forEach(r => {
      const sev = normalizeSeverity(r.severity);
      counts[sev] = (counts[sev] || 0) + 1;
    });
    const labels = SEVERITY_ORDER.filter(s => counts[s] !== undefined);
    Object.keys(counts).forEach(k => { if (!labels.includes(k)) labels.push(k); });
    return {
      labels,
      datasets: [{
        label: 'Records by Severity',
        data: labels.map(l => counts[l]),
        backgroundColor: labels.map(l => (SEVERITY_COLORS[l] || SEVERITY_COLORS.unknown).hex),
        borderRadius: 6,
        borderSkipped: false,
      }],
    };
  }, [filteredRecords]);

  const handleCategoryBarClick = (evt, elements, chart) => {
    if (!elements.length || !onCategoryClick) return;
    const index = elements[0].index;
    const label = chart.data.labels[index];
    onCategoryClick(label);
  };

  return (
    <>
      <PageHeader title="Analytics" description="Trends across categories, severity, and companies" />

      {/* Overall Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs font-medium">Total Records</p>
              <p className="text-2xl font-bold mt-1">{overallStats.totalRecords}</p>
            </div>
            <Zap size={28} className="opacity-30" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sky-100 text-xs font-medium">Companies</p>
              <p className="text-2xl font-bold mt-1">{overallStats.companies}</p>
            </div>
            <Building2 size={28} className="opacity-30" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-xs font-medium">Critical Issues</p>
              <p className="text-2xl font-bold mt-1">{overallStats.critical}</p>
            </div>
            <AlertTriangle size={28} className="opacity-30" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs font-medium">Categories</p>
              <p className="text-2xl font-bold mt-1">{overallStats.categories}</p>
            </div>
            <Tags size={28} className="opacity-30" />
          </div>
        </div>
      </div>

      {/* Company Filter */}
      <div className="mb-6 flex items-center gap-3 bg-white rounded-lg p-4 border border-slate-200">
        <Search size={18} className="text-slate-400" />
        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer font-medium"
        >
          <option value="">📊 All Companies ({allCompanies.length})</option>
          {allCompanies.map(company => (
            <option key={company} value={company}>🏢 {company}</option>
          ))}
        </select>
      </div>

      {/* Filtered Stats Cards */}
      {selectedCompany && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 bg-white rounded-xl p-4 border border-slate-200">
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-xs text-slate-500 font-medium">Total Records</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{filteredStats.totalRecords}</p>
          </div>
          <div className="border-l-4 border-red-500 pl-4">
            <p className="text-xs text-slate-500 font-medium">Critical</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{filteredStats.critical}</p>
          </div>
          <div className="border-l-4 border-orange-500 pl-4">
            <p className="text-xs text-slate-500 font-medium">High</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{filteredStats.high}</p>
          </div>
          <div className="border-l-4 border-amber-500 pl-4">
            <p className="text-xs text-slate-500 font-medium">Medium</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{filteredStats.medium}</p>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60 hover:shadow-md transition-shadow">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Tags size={16} className="text-blue-500" />By Category
          </h3>
          <p className="text-xs text-slate-400 -mt-3 mb-4">Click a bar to filter Intelligence by category</p>
          <div className="h-56">
            <Bar
              data={coloredCategoryData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                onClick: handleCategoryBarClick,
                plugins: {
                  legend: { display: false },
                  tooltip: { 
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    padding: 8,
                    titleFont: { size: 12 },
                    bodyFont: { size: 12 },
                    callbacks: { 
                      label: (ctx) => `${ctx.parsed.y} records`
                    }
                  },
                },
                scales: { 
                  y: { 
                    ticks: { stepSize: Math.max(1, Math.ceil(Math.max(...coloredCategoryData.datasets[0].data) / 5)) },
                    beginAtZero: true,
                  } 
                },
              }}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/60 hover:shadow-md transition-shadow">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" />By Severity
          </h3>
          <p className="text-xs text-slate-400 -mt-3 mb-4">Risk distribution</p>
          <div className="h-56">
            <Bar
              data={severityBarData}
              options={{
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: { 
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    padding: 8,
                    callbacks: { label: (ctx) => `${ctx.parsed.x} records` } 
                  },
                },
                scales: { 
                  x: { 
                    ticks: { stepSize: Math.max(1, Math.ceil(Math.max(...severityBarData.datasets[0].data) / 5)) },
                    beginAtZero: true,
                  } 
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Category Breakdown Table */}
      {selectedCompany && (
        <div className="mt-6 bg-white rounded-xl p-5 shadow-sm border border-slate-200/60">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Building2 size={16} className="text-blue-500" />{selectedCompany} - Category Breakdown
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {Object.entries(
              filteredRecords.reduce((acc, r) => {
                acc[r.category] = (acc[r.category] || 0) + 1;
                return acc;
              }, {})
            )
              .sort((a, b) => b[1] - a[1])
              .map(([category, count]) => (
                <div key={category} className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-3 border border-slate-200 hover:shadow-md transition-shadow">
                  <div className="text-xs font-medium text-slate-600 mb-2 truncate" title={category}>
                    {category}
                  </div>
                  <div className="flex items-end justify-between">
                    <div className="text-lg font-bold text-slate-800">{count}</div>
                    <div className="text-xs text-slate-500 font-medium">
                      {((count / filteredStats.totalRecords) * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
}

export default AnalyticsView;
